/**
 * File types a deposit keeps, identified by their content, not their extension.
 * Shared by the web deposit and the manual registry (apps/registry).
 */
export type ImageType = "image/png" | "image/jpeg" | "image/webp";
export type SidecarType = "text/plain" | "text/markdown" | "application/json" | "text/csv" | "application/pdf";
export type StoredType = ImageType | SidecarType | "application/zip";

export const EXTENSION: Record<ImageType, "png" | "jpg" | "webp"> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

const starts = (bytes: Uint8Array, ...signature: number[]) => signature.every((byte, i) => bytes[i] === byte);

export function detectMediaType(bytes: Uint8Array): ImageType | null {
  if (starts(bytes, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return "image/png";
  if (starts(bytes, 0xff, 0xd8, 0xff)) return "image/jpeg";
  if (starts(bytes, 0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return "image/webp";
  return null;
}

export const isZip = (bytes: Uint8Array) => starts(bytes, 0x50, 0x4b, 0x03, 0x04) || starts(bytes, 0x50, 0x4b, 0x05, 0x06);

/** Descriptive files worth keeping next to the slides (captions, sources, manifests). */
const SIDECAR_EXTENSIONS: Record<string, SidecarType> = {
  txt: "text/plain",
  md: "text/markdown",
  json: "application/json",
  csv: "text/csv",
  pdf: "application/pdf",
};

export function sidecarType(name: string, bytes: Uint8Array): SidecarType | null {
  const type = SIDECAR_EXTENSIONS[extensionOf(name)];
  if (!type) return null;
  if (type === "application/pdf" && !starts(bytes, 0x25, 0x50, 0x44, 0x46)) return null;
  return type;
}

/** Image formats a person might drop that the deposit does not take (reported, never silently dropped). */
export const UNSUPPORTED_IMAGE_EXTENSIONS = ["heic", "heif", "gif", "tif", "tiff", "bmp", "avif", "svg"];

export function extensionOf(name: string): string {
  const base = name.split("/").pop() ?? name;
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : "";
}
